import { store } from '../../../../../../Feature/Navigation/code/Views/Feature/Navigation/MainNavigation/MainNavigation.js';
import { toggleCartNotification } from '../../../../../../Feature/Navigation/code/Views/Feature/Navigation/MainNavigation/actions/TopNavigationActions.js';
import { LOCAL_STORAGE, PRODUCT_STATUS, ROLE_GUIDS } from '../../../../../React/code/Utilities/ExtranetConstants.js';
import { safeGetItem } from '../../../../../React/code/Utilities/LocalStorage.js';
import {
  addCartLines,
  canBuyServicePartRepair,
  fetchDataForAddToCartButton,
  fetchDataForAddToCartButtonInBatches,
} from '../../../Utils/Extranet/Utils.js';
import { AdobeAnalyticsUtil } from '/Foundation/Common/code/Views/Foundation/Common/AdobeAnalytics/AdobeAnalyticsUtil.js';
import { getUnavailabilityMessage } from '/Foundation/Common/code/Views/Foundation/Common/productAvailability';
import { isPosBuyButtonEnabledForMarket } from '/Foundation/Common/code/Utils/Extranet/posMarketRestrictions.js';

const itemsPopulated = [];
let productItems = [];
let user = safeGetItem(LOCAL_STORAGE.EXTRANET_USER);
let retailer = user?.CurrentSelectedRetailer;
const translations = window._EXTRANETDATA?._Products?.translations ?? window._THULEDATA?.TopNavigation?.Translations;
const isPointOfSalesSection = window._EXTRANETDATA?.isPointOfSalesSection;
let abortController = new AbortController();

// Compact-view image hover preview.
// Sets the --preview-src CSS custom property on each .slider-wrapper to
// the active <img>'s URL so the ::after pseudo-element in ProductList.less
// can render a 240x240 hover panel without us having to duplicate markup
// or move the original thumbnail. Re-runs whenever splide swaps the
// active slide (class change on its <img> elements).
const setupHoverPreview = (productItem) => {
  const wrapper = productItem.querySelector('.slider-wrapper');
  if (!wrapper || wrapper.dataset.previewBound === 'true') return;
  wrapper.dataset.previewBound = 'true';

  const updatePreviewSrc = () => {
    const activeImg =
      wrapper.querySelector('.splide__slide:not(.d-none) img:not(.d-none)') ||
      wrapper.querySelector('.product-image img:not(.d-none)') ||
      wrapper.querySelector('img');
    const src = activeImg?.currentSrc || activeImg?.getAttribute('src');
    if (src) {
      // CSS escape for url() — quote the string so spaces/parens in the URL don't break parsing.
      wrapper.style.setProperty('--preview-src', `url("${src}")`);
    }
  };

  updatePreviewSrc();
  // Re-run if any descendant image gets toggled (carousel slide change).
  const observer = new MutationObserver(updatePreviewSrc);
  observer.observe(wrapper, { attributes: true, subtree: true, attributeFilter: ['class', 'src'] });
};

export const recalcLineTotal = (productItem) => {
  const totalEl = productItem.querySelector('.total-price');
  if (!totalEl) return;
  const qtyInput = productItem.querySelector('.quantity');
  const currencyEl = productItem.querySelector('.currencycode');
  // Unit price is the retailer's selling price from M3 (set when the buy-area
  // data arrives), not the RRP rendered in .ga-price.
  const unit = parseFloat(productItem.dataset.unitSalesPrice) || 0;
  const qty = parseInt(qtyInput?.value, 10) || 0;
  const currency = currencyEl?.textContent?.trim() || '';
  if (!unit || !qty) {
    totalEl.textContent = '';
    return;
  }
  const total = unit * qty;
  const formatted = total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  totalEl.textContent = currency ? `${currency} ${formatted}` : formatted;
};

export const setupLineTotal = (productItem) => {
  const qtyInput = productItem.querySelector('.quantity');
  if (!qtyInput || qtyInput.dataset.totalBound === 'true') return;
  qtyInput.dataset.totalBound = 'true';
  qtyInput.addEventListener('input', () => recalcLineTotal(productItem));
  qtyInput.addEventListener('change', () => recalcLineTotal(productItem));
  recalcLineTotal(productItem); // initial value (may be empty until M3 returns)
};

export const setupQuantityStepper = (productItem) => {
  const stepper = productItem.querySelector('.qty-stepper');
  if (!stepper || stepper.dataset.stepperBound === 'true') return;
  stepper.dataset.stepperBound = 'true';

  const input = stepper.querySelector('.quantity');
  const decBtn = stepper.querySelector('.qty-decrease');
  const incBtn = stepper.querySelector('.qty-increase');
  if (!input) return;

  const min = Number(input.getAttribute('min')) || 1;
  const max = Number(input.getAttribute('max')) || Infinity;

  const setValue = (next) => {
    const clamped = Math.min(max, Math.max(min, next));
    if (input.value !== String(clamped)) {
      input.value = clamped;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  decBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    setValue((Number(input.value) || min) - 1);
  });
  incBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    setValue((Number(input.value) || min) + 1);
  });
};

export const setupBuyArea = (productItem, erpoutdoor, productType) => {
  itemsPopulated.push(productItem);

  setupQuantityStepper(productItem);
  setupLineTotal(productItem);
  setupHoverPreview(productItem);
  if (isPointOfSalesSection && !isPosBuyButtonEnabledForMarket(retailer)) {
    productItem.querySelector('.buy-area')?.classList.add('d-none');
    productItem.querySelector('.price-value > .loading-spinner')?.classList?.add('d-none');
    return;
  }

  if (isGhostItemProduct(productItem)) {
    const buyArea = productItem.getElementsByClassName('buy-area')[0];
    buyArea?.querySelector('.delivery-date')?.remove();
    buyArea?.querySelector('.tpd-product-stock-status')?.remove();
  }

  const isServicePartRepair = productItem?.dataset?.servicepartrepair === 'true';
  if (!canBuyServicePartRepair(isServicePartRepair)) {
    // Service Part Repair product but the selected retailer is not a service-repair center:
    // show "not in assortment" instead of the buy control and send no item-data request to M3.
    const buyArea = productItem.querySelector('.buy-area');
    if (buyArea) {
      buyArea.innerHTML = `<span class="paragraph--s text-color--gray80">${translations?.NotInAssortment ?? ''}</span>`;
    }
    productItem.querySelector('.price-value > .loading-spinner')?.classList?.add('d-none');
    return;
  }

  if (shouldFetchData(productItem) && retailer.B2BEnable) {
    if (isBundleProduct(productItem)) {
      getPricesForBundle(productItem);
    } else {
      fetchDataForAddToCartButton(
        erpoutdoor,
        onSuccess,
        onFailure,
        productType,
        undefined,
        undefined,
        undefined,
        undefined,
        isServicePartRepair
      );
    }
  } else {
    productItem.querySelector('.buy-area > .loading-spinner')?.classList?.add('d-none');
    productItem.querySelector('.price-value > .loading-spinner')?.classList?.add('d-none');
    if (productItem?.dataset?.productstatus === PRODUCT_STATUS.DISCONTINUED) {
      productItem.querySelector('.product-inc-quantity-add-to-cart').classList.remove('d-none');
      productItem.querySelector('.add-to-your-cart').classList.add('d-none');
    } else {
      productItem.querySelector('.delivery-date')?.remove();
    }
  }

  function getPricesForBundle(productItem) {
    const erpOutdoors = erpoutdoor.split(',');
    let products = [];
    erpOutdoors.forEach((erpOutdoor) => {
      products.push({ itemNumber: erpOutdoor.trim(), productType: productItem.dataset.producttype, quantity: 1 });
    });

    fetchDataForAddToCartButtonInBatches(products, onSuccessBundle, onFailure, '', '', abortController.signal);

    function onSuccessBundle(data) {
      const buyArea = productItem.getElementsByClassName('buy-area')[0];
      const deliveryDateElem = buyArea?.querySelector('.delivery-date');
      if (deliveryDateElem) {
        deliveryDateElem.remove();
      }

      const priceElem = productItem.getElementsByClassName('price-value')[0];

      let bundleProducts;
      if (productItem.dataset.bundleProducts) {
        //PDP
        bundleProducts = JSON.parse(productItem.dataset.bundleProducts);
      } else {
        //PLP
        bundleProducts = JSON.parse(productItem.parentElement.dataset.bundleProducts);
      }

      if (data.HasInvalidItems) {
        let hasErrors;
        if (isPointOfSalesSection) {
          hasErrors = data.ItemResponses.filter((item) => item.Error !== null)?.length > 0;
        }
        let shouldShowGeneralError =
          (hasErrors && !data.ItemResponses.find((item) => item.TranslatedErrorMessage !== null)) ||
          (!data.HasSalesPrice && !isPointOfSalesSection);
        if (!isPointOfSalesSection || (isPointOfSalesSection && (hasErrors || shouldShowGeneralError))) {
          if (shouldShowGeneralError) {
            let errorMessage = translations?.GeneralErrorText;
            buyArea.innerHTML = `<p class="error-message">${errorMessage}</p>`;
            priceElem?.remove();
          } else {
            const translatedErrorMessage = data.ItemResponses.find(
              (item) => item.TranslatedErrorMessage !== null
            )?.TranslatedErrorMessage;
            buyArea.innerHTML = '<p class="error-message">' + translatedErrorMessage + '</p>';
            priceElem.remove();
          }
          return;
        }
      }

      priceElem.innerHTML = data.TotalSalesPrice;
      productItem.dataset.unitSalesPrice = data.UnformattedTotalSalesPrice;
      buyArea.getElementsByClassName('loading-spinner')[0]?.remove();
      buyArea.getElementsByClassName('product-inc-quantity-add-to-cart')[0]?.classList.remove('d-none');
      recalcLineTotal(productItem);

      const qtyInput = productItem.querySelector('.quantity');
      qtyInput.addEventListener('blur', () => {
        if (qtyInput.value < 1) {
          productItem.querySelector('.quantity').value = 1;
        }
      });

      if (user?.HighestRole?.Id !== ROLE_GUIDS.DEALER_HIDE_PRICES) {
        productItem.querySelector('.add-to-cart-btn').addEventListener('click', (e) => {
          const quantity = productItem.querySelector('.quantity').value;
          let products = [];
          bundleProducts.forEach((product) => {
            products.push({ ProductId: product.ProductId, Quantity: product.NumberOfItems });
          });
          addToCart(e, products, quantity, productItem.dataset.productname, e.currentTarget);
        });
      }
      if (user?.HighestRole?.Id === ROLE_GUIDS.CUSTOMER_ORDER_REGISTRATION_USER) {
        productItem.querySelector('.add-to-cart-btn')?.classList.add('d-none');
        productItem.querySelector('.quantity')?.classList.add('d-none');
      }
    }
  }

  function onSuccess(data) {
    showDataFromM3(data, productItem);
  }

  function onFailure(error) {
    console.error('Error: ', error);
  }
};

function isBundleProduct(productItem) {
  const analyticsData = productItem.getElementsByClassName('analytics-data')[0]?.dataset;
  if (analyticsData?.isThuleBundle === 'True' && analyticsData?.isGhostItem !== 'True') {
    return true;
  }
  return false;
}

function isGhostItemProduct(productItem) {
  return productItem.querySelector('.analytics-data')?.dataset?.isGhostItem === 'True';
}

function shouldFetchData(productItem) {
  if (productItem?.dataset?.productstatus === PRODUCT_STATUS.DISCONTINUED) {
    return false;
  } else if (productItem?.dataset?.erpoutdoor === '') {
    return false;
  }
  return true;
}

const showDataFromM3 = (data, productItem) => {
  const buyArea = productItem.getElementsByClassName('buy-area')[0];
  const deliveryDateElem = buyArea?.querySelector('.delivery-date');
  const priceElem = productItem.getElementsByClassName('price-value')[0];

  if (buyArea) {
    const helperMessage = getUnavailabilityMessage(
      {
        statusErp: productItem?.dataset?.productstatus,
        hasAccess: data?.HasAccess,
        isAvailable: data?.IsAvailable,
      },
      translations
    );
    if (helperMessage && !isPointOfSalesSection) {
      buyArea.innerHTML = `<p class="error-message">${helperMessage}</p>`;
      priceElem?.remove();
    } else if (data.TranslatedErrorMessage && !isPointOfSalesSection) {
      buyArea.innerHTML = '<p class="error-message">' + data.TranslatedErrorMessage + '</p>';
      priceElem?.remove();
    } else if (
      data.Error ||
      (!data.HasSalesPrice && !isPointOfSalesSection) || //pos products are free and will never have a sales price. So exclude from this specific condition.
      (deliveryDateElem?.dataset?.showcominglaunch == 'False' && !data.DeliveryDate) ||
      (deliveryDateElem?.dataset?.showcominglaunch == 'True' && !deliveryDateElem?.dataset?.cominglaunchdate)
    ) {
      let errorMessage = translations?.GeneralErrorText;
      if (isPointOfSalesSection && data.TranslatedErrorMessage) {
        errorMessage = data.TranslatedErrorMessage; //override message for pos products incase we get this far.
      }
      buyArea.innerHTML = `<p class="error-message">${errorMessage}</p>`;
      priceElem?.remove();
    } else {
      if (priceElem) {
        if (data.HasSalesPrice && !data.TranslatedErrorMessage && !data.Error) {
          priceElem.innerHTML = data.SalesPrice;
        } else {
          priceElem.remove();
        }
      }

      if (data.Availability !== null && deliveryDateElem?.dataset.showcominglaunch == 'False') {
        const stockStatusElem = buyArea.querySelector('.stock-status');
        stockStatusElem.innerHTML =
          (data.IsAvailable ? '<i class="frg-icon-Check"></i> ' : '') + data.Availability + '</div>';
      }
      if (deliveryDateElem?.dataset?.showcominglaunch == 'False') {
        deliveryDateElem.innerHTML += data.DeliveryDate;
      }
      productItem.dataset.unitSalesPrice = data.UnformattedSalesPrice;
      buyArea?.getElementsByClassName('loading-spinner')[0]?.remove();
      buyArea?.getElementsByClassName('product-inc-quantity-add-to-cart')[0]?.classList.remove('d-none');
      recalcLineTotal(productItem);

      const qtyInput = productItem.querySelector('.quantity');
      qtyInput.addEventListener('blur', () => {
        if (qtyInput.value < 1) {
          productItem.querySelector('.quantity').value = 1;
        }
      });
      if (user?.HighestRole?.Id !== ROLE_GUIDS.DEALER_HIDE_PRICES) {
        productItem.querySelector('.add-to-cart-btn').addEventListener('click', (e) => {
          const quantity = productItem.querySelector('.quantity').value;
          addToCart(e, productItem.dataset.productid, quantity, productItem.dataset.productname, e.currentTarget);

          let analyticsData = productItem.getElementsByClassName('analytics-data')[0]?.dataset;
          AdobeAnalyticsUtil.DataLayer.TrackEvent('cart_addition', {
            add_to_cart_variant: getAddToCartVariant(productItem),
            products: [
              {
                id: productItem.dataset.productid,
                name: productItem.dataset.productname,
                quantity: quantity.toString(),
                price: normalizePrice(priceElem.innerText.trim()),
                original_price: normalizePrice(priceElem.innerText.trim()),
                over_category: analyticsData.overcategory,
                category: analyticsData.category,
                subcategory: analyticsData.subcategory,
                brand: analyticsData.brand,
                colour: analyticsData.color,
                stock_status: isNaN(productItem.querySelector('.stock-status')?.innerText.trim())
                  ? productItem.querySelector('.stock-status')?.innerText.trim()
                  : 'In stock',
              },
            ],
          });
        });
      }
      if (user?.HighestRole?.Id === ROLE_GUIDS.CUSTOMER_ORDER_REGISTRATION_USER) {
        productItem.querySelector('.add-to-cart-btn')?.classList.add('d-none');
        productItem.querySelector('.quantity')?.classList.add('d-none');
      }
    }
  }
};

const getAddToCartVariant = (productItem) => {
  let posPopMenu = document.querySelector('#REACT_posMainNavigation');
  if (!posPopMenu) {
    let analyticsData = productItem.getElementsByClassName('analytics-data')[0]?.dataset;
    return analyticsData.listname === 'Category page' ? 'Add_to_cart_cart_PLP' : 'Add_to_cart_cart_PDP';
  }

  let activeTab = posPopMenu.querySelector('.pos_pop_menu_item.active');
  let catalogType = activeTab.dataset.posPopCatalogType;

  return `Add_to_cart_${catalogType.replaceAll(' ', '_').toLowerCase()}`;
};

export const addToCart = (e, productId, quantity, productName, button) => {
  e.preventDefault();
  const loader = button.querySelector('.add-to-cart-loader');
  if (loader) {
    loader.classList.remove('d-none');
  }
  let product;

  let cartId = localStorage.getItem(LOCAL_STORAGE.CART_IDENTIFIER);

  if (isPointOfSalesSection) {
    cartId = localStorage.getItem(LOCAL_STORAGE.POS_CART_IDENTIFIER);
  }

  let totalQty = 0;
  if (Array.isArray(productId)) {
    const productAndProductQuantity = productId.reduce((acc, item) => {
      const id = item.ProductId;
      const qty = item.Quantity ?? 1; // fallback to 1 if no qty provided
      acc[id] = (acc[id] ?? 0) + qty;
      return acc;
    }, {});
    product = Object.entries(productAndProductQuantity).map(([key, value]) => {
      totalQty += value * parseInt(quantity);
      return {
        cartId,
        productId: key,
        quantity: value * parseInt(quantity),
      };
    });
  } else {
    totalQty = quantity;
    product = [
      {
        cartId: cartId,
        productId: productId,
        quantity: quantity,
      },
    ];
  }
  addCartLines(product, onSuccess, onFailure);

  function onSuccess() {
    store.dispatch(toggleCartNotification(true, totalQty, productName));
    if (loader) {
      loader.classList.add('d-none');
    }
  }

  function onFailure(error) {
    console.error('Error: ', error);
    if (loader) {
      loader.classList.add('d-none');
    }
  }
};

const normalizePrice = (price) => price?.replace(/[^\d.,-]|([.,])(?=.*\1)/g, '').replace(',', '.');

const isElementInViewport = (el) => {
  const rect = el.getBoundingClientRect();

  if (rect.height === 0) return false;

  const windowHeight = window.innerHeight || document.documentElement.clientHeight;

  //Check if any part of the product list item is in the viewport
  //Also subtract 30 in order to start the API call slightly before item is visible
  //for more seamless experience
  return !(
    Math.floor(100 - ((rect.top >= 0 ? 0 : rect.top) / -rect.height) * 100) < 1 - 30 ||
    Math.floor(100 - ((rect.bottom - windowHeight) / rect.height) * 100) < 1 - 30
  );
};

document.addEventListener('DOMContentLoaded', function () {
  if (window._THULEDATA.isExtranet) {
    window.addEventListener('fetchPricesOnFilter', (e) => {
      enableLoadingOnScroll(e.detail);
    });

    getProductItems();
    enableLoadingOnScroll(productItems);
    checkUserRole();
  }
});

function enableLoadingOnScroll(products) {
  //Fetch M3 data for all products visible in the initial viewport OR for the first 6, whichever number is highest
  //Then do the API calls dynamically when scrolling
  let numOfItemsInViewport = 0;
  let initialItems = [];
  products.forEach((item) => {
    if (item !== null && item !== undefined) {
      if (isElementInViewport(item)) {
        numOfItemsInViewport++;
        initialItems.push(item);
      } else {
        //Product slider items should not be loaded unless they are already in the viewport
        //or if you scroll to them
        if (!item.classList.contains('productSliderItem')) {
          initialItems.push(item);
        }
      }
    }
  });
  let firstItemsToLoad = [];
  if (numOfItemsInViewport > 6) {
    firstItemsToLoad = initialItems.slice(0, numOfItemsInViewport);
  } else {
    firstItemsToLoad = initialItems.slice(0, 6);
  }

  if (firstItemsToLoad.length > 0) {
    firstItemsToLoad.forEach((item) => {
      if (!itemsPopulated.includes(item)) {
        const productType = item.dataset.producttype === 'Spare part' ? item.dataset.producttype : '';
        let erpOutdoor = item.dataset.erpoutdoor;
        setupBuyArea(item, erpOutdoor, productType);
      }
    });
  }

  window.addEventListener('scroll', () => {
    products.forEach((item) => {
      if (item !== null && item !== undefined) {
        if (isElementInViewport(item)) {
          if (!itemsPopulated.includes(item)) {
            const productType = item.dataset.producttype === 'Spare part' ? item.dataset.producttype : '';
            let erpOutdoor = item.dataset.erpoutdoor;
            setupBuyArea(item, erpOutdoor, productType);
          }
        }
      }
    });
  });
}

function checkUserRole() {
  if (user?.HighestRole?.Id === ROLE_GUIDS.DEALER_HIDE_PRICES) {
    productItems.forEach((element) => element.querySelector('.price-value').remove());
    productItems.forEach((element) => element.querySelector('.total-price')?.remove());
    productItems.forEach((element) => (element.querySelector('.add-to-cart-btn').disabled = true));
  }
}

function getProductItems() {
  if (isPointOfSalesSection) {
    productItems = Array.from(document.querySelectorAll('.product-list-item, .productSliderItem, #productBuyArea'));
  } else {
    productItems = Array.from(
      document.querySelectorAll('.product-list-item, .productSliderItem, #productBuyArea, .pdp__bundleItem')
    );
  }
}
